// First-run onboarding — a single-page checklist.
//
//   1 · Install dense        copy → run it → approve in the browser tab
//   2 · Choose your agent    explicit pick, no default        → advances
//   3 · Start <agent>        run it once; the first request finishes it
//
// Step 1 completes on a REAL signal (the `dense setup` device-flow key
// landing on /me), with "I ran it" as the manual floor — copy alone never
// advances. Step 3 waits on usage.request_count rising above the baseline
// snapshotted at mount, so a returning user's history never auto-skips.

import React from "react";
import { Brand } from "../components/Brand";
import { AgentPromptRow } from "../components/AgentPrompt";
import { CopyPill } from "../components/CopyPill";
import { Modal } from "../components/Modal";
import { Button } from "../design/Button";
import { Icon } from "../design/icons";
import { Segmented } from "../design/Tabs";
import { detectPlatform, type Platform } from "../lib/platform";
import { cliUrl } from "../lib/urls";
import { ackOnboarding } from "../lib/api";
import {
  clearWizard,
  denseKeyIds,
  denseLinked,
  HARNESSES,
  OTHER_HARNESS,
  resolveHarness,
  type Harness,
  WIZARD_KEY,
} from "../lib/onboarding";
import type { AuthHeaders } from "../lib/auth";
import type { Me } from "../lib/types";
import {
  CHOOSE_AGENT_PROMPT,
  installAgentPrompt,
  startAgentPrompt,
} from "../lib/setupPrompts";

type Step = 1 | 2 | 3;

function requestCount(me: Me): number {
  return me.usage?.request_count ?? 0;
}

// ── resume persistence ──────────────────────────────────────────────
// /cli (the printed sign-in link, if someone opens it) reads this key to
// name the chosen agent; a same-tab return or refresh restores step + picks.
type WizardState = {
  step: Step;
  os: Platform;
  harnessId: string;
  copied: boolean;
};

function loadWizard(): Partial<WizardState> {
  try {
    const raw = localStorage.getItem(WIZARD_KEY);
    return raw ? (JSON.parse(raw) as Partial<WizardState>) : {};
  } catch {
    return {};
  }
}
function saveWizard(s: WizardState): void {
  try {
    localStorage.setItem(WIZARD_KEY, JSON.stringify(s));
  } catch {
    /* private mode / storage disabled — resume is best-effort */
  }
}

// ── install command per platform ────────────────────────────────────
function installCmd(os: Platform): {
  command: string;
  host: string;
  prompt: string;
} {
  const base = cliUrl();
  return os === "windows"
    ? {
        command: `irm ${base}/nt | iex`,
        host: `${base}/nt`,
        prompt: "PS>",
      }
    : {
        command: `curl -fsSL ${base}/unix | sh`,
        host: `${base}/unix`,
        prompt: "$",
      };
}

type StepStatus = "pending" | "active" | "done";
function statusCls(s: StepStatus): string {
  return s === "done" ? "is-done" : s === "active" ? "is-active" : "is-pending";
}

// ── live terminal preview — mirrors the same progression ────────────
function Terminal({
  step,
  os,
  harness,
  copied,
  linked,
  live,
  email,
}: {
  step: Step;
  os: Platform;
  harness: Harness;
  copied: boolean;
  linked: boolean;
  live: boolean;
  email: string | null;
}) {
  const install = installCmd(os);
  const ran = step >= 2 || copied; // install one-liner is in their terminal
  const wrapping = step >= 3; // agent chosen, wrap command surfaced
  const start = harness.tool ?? harness.wrap;

  return (
    <div className="ob-term" aria-hidden="true">
      <div className="ob-term-bar">
        <div className="ob-dots">
          <i />
          <i />
          <i />
        </div>
        <div className="ob-term-title">zsh — acme-app</div>
        <div className="ob-watch">
          <b />
          WATCHING
        </div>
      </div>

      <div className="ob-term-body">
        {ran ? (
          <>
            <div className="tl">
              <span className="tl-prompt">~ $ </span>
              <span className="tl-cmd">{install.command}</span>
            </div>
            <div className="tl tl-dim">downloading dense … done</div>
            <div className="tl tl-ok">
              <Icon name="check" size={12} /> dense installed
            </div>
            <div className="tl tl-dim">
              dense setup · approve in the browser tab
            </div>
            {linked || step >= 2 ? (
              <div className="tl tl-ok">
                ✓ authorized{email ? ` as ${email}` : ""}
              </div>
            ) : (
              <div className="tl">
                <span className="tl-dim">waiting for authorization </span>
                <span className="tl-cursor" />
              </div>
            )}
            {wrapping ? (
              <>
                <div className="tl gap" />
                <div className="tl tl-dim">
                  next: run `{start}` inside a project
                </div>
                <div className="tl gap" />
                <div className="tl">
                  <span className="tl-path">~/acme-app $ </span>
                  {live ? (
                    <span className="tl-cmd">{start}</span>
                  ) : (
                    <span className="tl-cursor" />
                  )}
                </div>
                {live ? (
                  <div className="tl tl-ok">
                    ✓ session live — compressing every call
                  </div>
                ) : null}
              </>
            ) : step >= 2 ? (
              <>
                <div className="tl gap" />
                <div className="tl">
                  <span className="tl-prompt">~ $ </span>
                  <span className="tl-cursor" />
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div className="tl">
            <span className="tl-prompt">~ $ </span>
            <span className="tl-cursor" />
          </div>
        )}
      </div>

      <div
        className={`ob-term-status ${live || (step === 1 && linked) ? "live" : ""}`}
      >
        <i />
        {live ? (
          <span>session live — savings streaming in</span>
        ) : wrapping ? (
          <span>
            waiting for <code>{start}</code>
          </span>
        ) : step === 2 ? (
          <span>installed, now choose your agent</span>
        ) : linked ? (
          <span>dense signed in from your terminal</span>
        ) : copied ? (
          <span>copied, now run it and approve in the browser tab</span>
        ) : (
          <span>waiting for the install command</span>
        )}
      </div>
    </div>
  );
}

// ── shell ───────────────────────────────────────────────────────────
export function Onboarding({
  me,
  authHeaders,
  refresh,
  onComplete,
}: {
  me: Me;
  authHeaders: AuthHeaders;
  refresh: () => void | Promise<void>;
  onComplete: () => void;
}) {
  const [restored] = React.useState<Partial<WizardState>>(loadWizard);
  const [harnessId, setHarnessId] = React.useState<string>(
    restored.harnessId ?? "",
  );
  // A restored step 3 with no pick (pre-fix state) drops back to choosing.
  const [step, setStep] = React.useState<Step>(
    restored.step === 3 && restored.harnessId
      ? 3
      : restored.step === 2 || restored.step === 3
        ? 2
        : 1,
  );
  const [os, setOs] = React.useState<Platform>(restored.os ?? detectPlatform());
  const [copied, setCopied] = React.useState<boolean>(restored.copied ?? false);
  const [entering, setEntering] = React.useState(false);
  // Baseline: only requests that arrive AFTER this mount count as the
  // first real session — a returning user's history never auto-completes.
  const [baseReq] = React.useState(() => requestCount(me));
  const [baseKeys] = React.useState(() => new Set(denseKeyIds(me)));

  React.useEffect(() => {
    saveWizard({ step, os, harnessId, copied });
  }, [step, os, harnessId, copied]);

  const live = requestCount(me) > baseReq;
  const linked = denseLinked(me, baseKeys);
  const harness = resolveHarness(harnessId);
  const install = installCmd(os);
  // Until the agent step is passed, the wrap step is generic.
  const wrapName = step >= 3 ? harness.name : "your agent";

  const advance = (to: Step) => setStep((s) => (s < to ? to : s));

  // `dense setup` signed this account in from the terminal — install is proven.
  React.useEffect(() => {
    if (linked && step === 1) advance(2);
  }, [linked, step]);

  const installStatus: StepStatus = step > 1 ? "done" : "active";
  const agentStatus: StepStatus =
    step > 2 ? "done" : step === 2 ? "active" : "pending";
  const wrapStatus: StepStatus = live
    ? "done"
    : step === 3
      ? "active"
      : "pending";

  const pickAgent = (id: string) => {
    setHarnessId(id);
    advance(3);
  };

  const finish = React.useCallback(async () => {
    setEntering(true);
    try {
      await ackOnboarding(authHeaders, "onboarding_complete");
    } catch {
      /* best-effort — never trap the user in setup */
    }
    clearWizard();
    await refresh();
    onComplete();
  }, [authHeaders, refresh, onComplete]);

  const back = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  const gated = !live;

  // "I ran it" is not proof. The dialog keeps watching for the sign-in and
  // only lets a manual continue through after both checks are ticked.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [ranOk, setRanOk] = React.useState(false);
  const [signedOk, setSignedOk] = React.useState(false);
  const closeConfirm = React.useCallback(() => setConfirmOpen(false), []);
  const confirmInstall = () => {
    setConfirmOpen(false);
    advance(2);
  };

  return (
    <div className="ob-shell">
      <header className="ob-topbar">
        <div className="ob-brand">
          <Brand />
        </div>
        <button
          type="button"
          className="ob-skip"
          onClick={finish}
          disabled={entering}
        >
          Skip setup
        </button>
      </header>

      <main className="ob-main">
        <div className="ob-left">
          <div className="ob-eyebrow">Get set up</div>
          <h1 className="ob-h1">Follow along in your terminal</h1>

          <div className="ob-list">
            {/* Step 1 · Install */}
            <div className={`ob-step ${statusCls(installStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {installStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "1"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Install dense</div>
                {installStatus === "done" ? (
                  <div className="ob-stepmeta">
                    {linked ? "Signed in from your terminal" : "Installed"}
                  </div>
                ) : (
                  <div className="ob-stepaction">
                    <CopyPill
                      command={install.command}
                      host={install.host}
                      prompt={install.prompt}
                      onCopied={() => setCopied(true)}
                      head={
                        <Segmented
                          size="sm"
                          label="Operating system"
                          toggle
                          value={os}
                          onChange={setOs}
                          options={[
                            { value: "unix", label: "Mac / Linux" },
                            { value: "windows", label: "Windows" },
                          ]}
                        />
                      }
                      foot={
                        <AgentPromptRow
                          label="Or let your agent do it"
                          prompt={installAgentPrompt(install.command)}
                          onCopied={() => setCopied(true)}
                        />
                      }
                    />
                    {copied ? (
                      <div className="ob-wait">
                        <i />
                        <span>
                          Press Approve in the browser tab that opens. This page
                          moves on by itself. No tab? Open the link dense
                          prints.
                        </span>
                      </div>
                    ) : (
                      <div className="ob-stepmeta">
                        The install opens a browser tab to approve, then this
                        page moves on by itself.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 · Choose agent */}
            <div className={`ob-step ${statusCls(agentStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {agentStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "2"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Choose your coding agent</div>
                {agentStatus === "done" ? (
                  <div className="ob-stepmeta">{harness.name}</div>
                ) : agentStatus === "active" ? (
                  <div className="ob-stepaction">
                    <div
                      className="ob-agents"
                      role="radiogroup"
                      aria-label="Coding agent"
                    >
                      {HARNESSES.map((h) => (
                        <button
                          type="button"
                          key={h.id}
                          role="radio"
                          aria-checked={harnessId === h.id}
                          className="ob-agent"
                          onClick={() => pickAgent(h.id)}
                        >
                          <span className="ob-agent-badge">
                            {h.logo ? (
                              <img src={h.logo} alt="" width={20} height={20} />
                            ) : (
                              <span aria-hidden="true">+</span>
                            )}
                          </span>
                          <span className="ob-agent-name">{h.name}</span>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="ob-agent-other"
                      onClick={() => pickAgent(OTHER_HARNESS.id)}
                    >
                      I use a different agent →
                    </button>
                    <AgentPromptRow
                      label="Not sure? Ask your agent"
                      prompt={CHOOSE_AGENT_PROMPT}
                    />
                  </div>
                ) : (
                  <div className="ob-stepmeta">
                    dense wraps the agent you already run
                  </div>
                )}
              </div>
            </div>

            {/* Step 3 · Wrap */}
            <div className={`ob-step ${statusCls(wrapStatus)}`}>
              <div className="ob-marker">
                <span className="ob-dot">
                  {wrapStatus === "done" ? (
                    <Icon name="check" size={12} />
                  ) : (
                    "3"
                  )}
                </span>
                <span className="ob-rail" />
              </div>
              <div className="ob-stepbody">
                <div className="ob-steptitle">Start {wrapName}</div>
                {wrapStatus === "active" ? (
                  <div className="ob-stepaction">
                    <div className="ob-stepmeta">
                      Open a project and start it. Setup already routes the bare
                      command through dense if you ticked it:
                    </div>
                    <CopyPill
                      command={harness.tool ?? harness.wrap}
                      prompt="$"
                    />
                    {harness.tool ? (
                      <div className="ob-stepmeta">
                        Didn’t tick it? <code>{harness.wrap}</code> works per
                        session, or run{" "}
                        <code>dense persist {harness.tool}</code> once.
                      </div>
                    ) : null}
                    <AgentPromptRow
                      label="Or let your agent start it"
                      prompt={startAgentPrompt(harness)}
                    />
                    <div className="ob-wait">
                      <i />
                      <span>
                        Waiting for your first request. Send one message and
                        this step ticks itself off.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="ob-stepmeta">
                    {wrapStatus === "done"
                      ? "First request came through — you're done"
                      : "Run it once; the first request finishes setup"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {step > 1 || copied ? (
            <div className="ob-foot">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  icon="chevron-left"
                  onClick={back}
                  disabled={entering}
                >
                  Back
                </Button>
              ) : null}
              <div className="ob-foot-right">
                {step === 1 ? (
                  <Button
                    className="ob-cta"
                    onClick={() => setConfirmOpen(true)}
                  >
                    I ran it, continue
                  </Button>
                ) : step === 2 ? (
                  <span className="ob-stepmeta">Pick an agent to continue</span>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      iconRight="arrow-right"
                      onClick={() => void finish()}
                      disabled={entering}
                    >
                      Skip for now
                    </Button>
                    <Button
                      variant="primary"
                      className="ob-cta"
                      onClick={() => void finish()}
                      disabled={entering || gated}
                      title={
                        gated
                          ? "Send one message through dense to continue"
                          : undefined
                      }
                    >
                      {entering ? "Entering…" : "Enter dashboard"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="ob-right">
          <Terminal
            step={step}
            os={os}
            harness={harness}
            copied={copied}
            linked={linked}
            live={live}
            email={me.email ?? null}
          />
        </div>
      </main>

      {confirmOpen && step === 1 ? (
        <Modal
          eyebrow="Step 1"
          title="Did dense sign in?"
          onClose={closeConfirm}
          footer={
            <>
              <Button variant="ghost" onClick={closeConfirm}>
                Not yet, go back
              </Button>
              <Button
                variant="primary"
                onClick={confirmInstall}
                disabled={!(ranOk && signedOk)}
              >
                Yes, I signed in, continue
              </Button>
            </>
          }
        >
          <div className="ob-wait">
            <i />
            Still watching for the sign-in from your terminal. This closes on
            its own the moment it lands.
          </div>
          <p className="ob-confirm-why">
            The installer ends by running <code>dense setup</code>, which opens
            a browser tab to approve this terminal. Until you approve it,{" "}
            <code>dense</code> can’t route your agent through condense.
          </p>
          <label className="ob-check">
            <input
              type="checkbox"
              checked={ranOk}
              onChange={(e) => setRanOk(e.target.checked)}
            />
            The install command finished without errors
          </label>
          <label className="ob-check">
            <input
              type="checkbox"
              checked={signedOk}
              onChange={(e) => setSignedOk(e.target.checked)}
            />
            <span>
              <code>dense setup</code> opened the approval page and I pressed
              Approve
            </span>
          </label>
        </Modal>
      ) : null}
    </div>
  );
}
